import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { ToastShell } from './ToastShell';
import { Button } from '../Button';

import type { ToastProps, ToastVariant } from './Toast.types';

/**
 * The variant's icon ink. The shell's rail carries the variant's colour too;
 * the icon's shape carries it for anyone who can't tell the colours apart.
 * "Semantic palettes communicate state, not chrome" — colour-system.md
 */
const ICON_CLASSES: Record<ToastVariant, string> = {
  default: 'text-neutral-400',
  success: 'text-success-400',
  error: 'text-error-400',
  warning: 'text-warning-400',
  info: 'text-info-400',
};

/**
 * Default icons for each variant
 */
const defaultIcons: Record<ToastVariant, React.ReactElement> = {
  default: <InformationCircleIcon className="h-5 w-5" />,
  success: <CheckCircleIcon className="h-5 w-5" />,
  error: <XCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  info: <InformationCircleIcon className="h-5 w-5" />,
};

/**
 * Toast component for displaying transient notification messages
 *
 * Drawn on `ToastShell`, which owns the surface, the clock, dismiss, the role
 * and the rail; the toast brings its icon, its copy and its one action.
 *
 * @example
 * ```tsx
 * <Toast
 *   id="1"
 *   variant="success"
 *   message="Your changes have been saved!"
 *   onRemove={(id) => handleRemove(id)}
 * />
 * ```
 */
export function Toast({
  id,
  variant = 'default',
  title,
  message,
  duration = 4000,
  action,
  dismissible = true,
  icon,
  onDismiss,
  onRemove,
}: ToastProps): React.JSX.Element {
  return (
    <ToastShell
      variant={variant}
      duration={duration}
      dismissible={dismissible}
      onDismiss={onDismiss}
      onRemove={() => onRemove(id)}
      data-testid={`toast-${id}`}
    >
      {({ dismiss }) => (
        <>
          <span
            aria-hidden="true"
            className={`flex-shrink-0 ${ICON_CLASSES[variant]}`}
          >
            {icon ?? defaultIcons[variant]}
          </span>

          {/* The notification toast's hierarchy: a title leads and the message
              under it is secondary; on its own, the message is the lead. */}
          <div className="min-w-0 flex-1">
            {title ? (
              <>
                <p className="text-body-md font-medium leading-snug text-neutral-50">
                  {title}
                </p>
                <p className="mt-0.5 text-body-sm text-neutral-400">
                  {message}
                </p>
              </>
            ) : (
              <p className="text-body-md text-neutral-50">{message}</p>
            )}

            {/* Bordered, so it reads as a button before anyone hovers it —
                the treatment an invitation's Accept already uses. */}
            {action && (
              <Button
                variant="outline-primary"
                size="xs"
                className="mt-2"
                onClick={() => {
                  action.onClick();
                  dismiss();
                }}
              >
                {action.label}
              </Button>
            )}
          </div>
        </>
      )}
    </ToastShell>
  );
}

export default Toast;
